'use client';

import { useState } from 'react';
import { CalendarClock, Info } from 'lucide-react';
import { Button } from '@/components/Button/ButtonV2';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/Card/Card';
import { useConfirmation } from '@/contexts';
import { VendorOnDemandHoursI } from '@/types/cyclone/models';
import { EmptyObject, ErrorI } from '@/types/cyclone/requests';
import { useMutation } from 'react-query';
import { useClient } from '@/hooks';
import { AvailabilityModal } from './AvailabilityModal';
import dayjs from 'dayjs';
import useLog from '@/hooks/useLog';
import { toast } from 'react-toastify';
import { useService } from '@/containers/ActionServiceV2/context';
import { Loader } from '@/components/Spinner/Loader';
import { Column } from '@/components';

export const AvailabilityTab: React.FC = () => {
  const { confirm } = useConfirmation();
  const { client } = useClient();
  const { logAndNotify } = useLog();
  const { upcomingAvailabilities, pastAvailabilities, service, refetchService } = useService();

  const [activeTab, setActiveTab] = useState('upcoming');

  // Estados para diálogos
  const [showAddDialog, setShowAddDialog] = useState<boolean>(false);
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [selectedAvailability, setSelectedAvailability] = useState<VendorOnDemandHoursI | null>(null);

  const mutationDeleteOnDemandHour = useMutation<EmptyObject, ErrorI, { id: string }>(
    (data) =>
      client(`me/vendor/on_demand_hours/${data.id}`, 'DELETE', {
        isAuthRequired: true
      }),
    {
      onSuccess: () => {
        toast.success('Se eliminó el horario con éxito.');
        refetchService();
      },
      onError: (e) => {
        logAndNotify(`Error al eliminar un horario especifico del servicio - ${JSON.stringify(e)}`);
      }
    }
  );

  const handleAddAvailability = () => {
    setShowAddDialog(true);
  };

  const handleEditAvailability = (availability: VendorOnDemandHoursI) => {
    setSelectedAvailability(availability);
    setShowEditDialog(true);
  };

  const handleDeleteAvailability = (availability: VendorOnDemandHoursI) => {
    confirm({
      status: 'danger',
      content: '¿Estás seguro/a que deseas eliminar esta disponibilidad?',
      message: `Esta acción eliminará la disponibilidad configurada para el
          ${dayjs(availability.start_at).format('dddd DD [de] MMMM')}. Esta acción no se puede deshacer.`,
      title: 'Eliminar disponibilidad',
      confirmText: 'Si, eliminar',
      onConfirm: () => handleConfirmDelete(availability.id.toString())
    });
  };

  const handleConfirmDelete = (id: string) => {
    mutationDeleteOnDemandHour.mutate({ id });
  };

  if (!service) {
    return (
      <div className="w-full h-full">
        <Loader fullHeight />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pt-4 px-2 sm:px-0 w-full sm:max-w-[672px] mx-auto">
      <div className="w-full sm:border-2 sm:border-gray-200 sm:rounded-xl sm:p-6 sm:bg-white">
        <Column className="pb-8" gap={20}>
          <Column>
            <span className="text-xl font-medium">Disponibilidad del servicio</span>
            <label className="font-normal text-xs text-[#858585] w-full md:w-[80%]">
              Consulta y gestiona los horarios disponibles para este servicio
            </label>
          </Column>
        </Column>

        {service?.vendor_on_demand_hours && service.vendor_on_demand_hours.length === 0 && (
          <div className="flex items-start gap-2 p-4 bg-blue/20 border border-blue-200 text-blue rounded mb-6">
            <Info className="h-4 w-4 mt-0.5" />
            <p>
              Este servicio aún no tiene horarios disponibles configurados. Puedes agregarlos usando el botón
              Agregar disponibilidad.
            </p>
          </div>
        )}

        <div className="flex justify-end mb-6">
          <Button onClick={handleAddAvailability} className="gap-2">
            Agregar disponibilidad
          </Button>
        </div>

        <div className="mt-6">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="grid w-full grid-cols-2 max-w-md">
              <button
                onClick={() => setActiveTab('upcoming')}
                className={`flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium ${
                  activeTab === 'upcoming'
                    ? 'border-b-2 border-blue text-blue'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Próximos
                {upcomingAvailabilities.length > 0 && (
                  <span className="inline-flex items-center justify-center px-2 py-0.5 ml-1 text-xs font-medium bg-gray-100 text-gray-400 border rounded-full">
                    {upcomingAvailabilities.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('past')}
                className={`flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium ${
                  activeTab === 'past'
                    ? 'border-b-2 border-blue text-blue'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pasados
                {pastAvailabilities.length > 0 && (
                  <span className="inline-flex items-center justify-center px-2 py-0.5 ml-1 text-xs font-medium bg-gray-100 text-gray-400 border rounded-full">
                    {pastAvailabilities.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Tab Content: Upcoming */}
          <div className={`mt-6 ${activeTab === 'upcoming' ? 'block' : 'hidden'}`}>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <CalendarClock className="h-5 w-5 text-blue" />
                <h2 className="text-lg font-medium">Días y horarios próximos</h2>
              </div>

              {upcomingAvailabilities.length === 0 ? (
                <div className="flex items-start gap-2 p-4 bg-gray-50 border border-gray-200 text-gray-700 rounded">
                  <p>No hay fechas con disponibilidad configurada para los próximos días.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingAvailabilities.map((availability) => (
                    <Card key={availability.id} className="overflow-hidden">
                      <CardHeader className="pb-3">
                        <div className="flex justify-between items-center">
                          <CardTitle className="text-lg">
                            <span className="capitalize">
                              {dayjs(availability.start_at).format('dddd')}
                              {` `}
                            </span>
                            {dayjs(availability.start_at).format('D [de] MMMM')}
                          </CardTitle>
                          <span>Próximo</span>
                        </div>
                      </CardHeader>
                      <CardContent className="pb-3">
                        <div className="space-y-2">
                          {availability.working_hours.map((slot, i) => (
                            <div key={i} className="flex items-center gap-2 p-2 bg-gray-100 rounded-md">
                              <span className="font-medium">
                                {slot.start_time} - {slot.end_time}
                              </span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                      <CardFooter className="flex justify-end gap-2 pt-0">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex gap-1"
                          onClick={() => handleEditAvailability(availability)}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleDeleteAvailability(availability)}
                        >
                          Eliminar
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Tab Content: Past */}
          <div className={`mt-6 ${activeTab === 'past' ? 'block' : 'hidden'}`}>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <CalendarClock className="h-5 w-5 text-[#868686]" />
                <h2 className="text-lg font-medium">Días y horarios pasados</h2>
              </div>

              {pastAvailabilities.length === 0 ? (
                <div className="flex items-start gap-2 p-4 bg-gray-50 border border-gray-200 text-gray-700 rounded">
                  <p>No hay fechas con disponibilidad configurada para días pasados.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pastAvailabilities.map((availability) => (
                    <Card key={availability.id} className="overflow-hidden border-muted bg-muted/10">
                      <CardHeader className="pb-3">
                        <div className="flex justify-between items-center">
                          <CardTitle className="text-lg text-[#868686]">
                            <span className="capitalize">
                              {dayjs(availability.start_at).format('dddd')}
                              {` `}
                            </span>
                            {dayjs(availability.start_at).format('D [de] MMMM')}
                          </CardTitle>
                          {/* <span className="bg-muted text-[#868686]">Pasado</span> */}
                        </div>
                      </CardHeader>
                      <CardContent className="pb-3">
                        <div className="space-y-2">
                          {availability.working_hours.map((slot, i) => (
                            <div
                              key={i}
                              className="flex items-center gap-2 p-2 bg-muted/30 rounded-md text-[#868686]"
                            >
                              <span>
                                {slot.start_time} - {slot.end_time}
                              </span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <AvailabilityModal
          show={showAddDialog}
          setShow={setShowAddDialog}
          refetch={refetchService}
          serviceId={service.id}
        />
        <AvailabilityModal
          show={showEditDialog}
          setShow={setShowEditDialog}
          onDemandHour={selectedAvailability as VendorOnDemandHoursI}
          refetch={refetchService}
          serviceId={service.id}
        />
      </div>
    </div>
  );
};
